/* eslint-disable security/detect-object-injection */
import {useMemo, useCallback} from 'react';
import {
  createSignal,
  useReactiveSignal,
  type ReactSetSignal,
} from 'react-set-signal';

// Types
// -------------------------
type SignalWithId<T> = ReactSetSignal<T> & {id: string};
type GlobalStore = Record<string, SignalWithId<any>>;

declare global {
  interface Window {
    __SIGNAL_STORE__: GlobalStore;
  }
}

// -------------------------
// Safe Global Signal Store
// -------------------------
const getGlobalStore = (): GlobalStore => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    // Reuse the opener's store only if it is accessible (same-origin).
    if (window.opener?.__SIGNAL_STORE__) {
      return window.opener.__SIGNAL_STORE__;
    }
  } catch {
    // Ignore SecurityError when opener is cross-origin
    // (e.g. Google Tag Assistant, external popups, etc.)
  }

  return {};
};

const globalStore: GlobalStore = getGlobalStore();

if (typeof window !== 'undefined') {
  window.__SIGNAL_STORE__ = globalStore;
}

// ----------------------------
// usePopoutDimensions
// ----------------------------
export const useSignalStore = <T>(
  id: string,
  initialState: T
): [T, (value: T | ((prev: T) => T)) => void] => {
  const $signal = useMemo(() => {
    if (globalStore[id] !== undefined) {
      return globalStore[id] as SignalWithId<T>;
    }

    // Create new signal
    const signal = createSignal(initialState) as SignalWithId<T>;
    signal.id = id;
    globalStore[id] = signal;
    return signal;
  }, [id, initialState]);

  // Subscribe to signal changes for reactive updates
  const value = useReactiveSignal($signal);

  // Memoized setter that handles both direct values and updater functions
  const setValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      $signal.set(newValue);
    },
    [$signal]
  );

  return [value, setValue];
};
// ---------------

export const getSignal = <T>(id: string): SignalWithId<T> | undefined => {
  return globalStore[id] as SignalWithId<T> | undefined;
};

// ---------------

export const deleteSignal = (id: string): boolean => {
  if (globalStore[id]) {
    delete globalStore[id];
    return true;
  }
  return false;
};
// ---------------

export const getSignalIds = (): string[] => {
  return Object.keys(globalStore);
};
// ---------------

export const clearSignalStore = (): void => {
  Object.keys(globalStore).forEach((key) => {
    delete globalStore[key];
  });
};
// ---------------
